/**
 * The pop-up's Estimate tab gets two tables from the old admin portal when a line changed: "Original estimate" and
 * "Current estimate". The admin had to read both and compare by eye. This folds them into one: each changed rate,
 * quantity or row total in the current table shows the vendor's first value struck out before the new one ("4.00 2.00"),
 * and the original table is tucked into a closed "Original estimate" section for reference.
 * (Nahid, 29 Oct: show the original quantity struck out beside the countered one.)
 *
 * Pure DOM work on the already-parsed estimate, so it can be tested without the widget.
 */

const RATE = 2;
const QTY = 3;
const TOTAL = 4;

const text = (cell: Element | undefined): string => (cell?.textContent ?? '').replace(/\s+/g, ' ').trim();
const num = (s: string): number | null => {
  const n = Number(s.replace(/[$,\s]/g, ''));
  return s && Number.isFinite(n) ? Math.round(n * 100) / 100 : null;
};
/** Same line in both tables: item and description with every number blanked (the labor text carries hours and techs). */
const lineKey = (row: HTMLTableRowElement): string =>
  [row.cells[0], row.cells[1]].map((c) => text(c).replace(/[\d.,]+/g, '#').toLowerCase()).join('|');
const dataRows = (table: Element): HTMLTableRowElement[] =>
  Array.from(table.querySelectorAll<HTMLTableRowElement>('tbody tr')).filter((tr) => tr.cells.length >= 5);

/** Returns true when the two tables were merged. Leaves the markup alone when there is nothing to merge. */
export function mergeOriginalIntoCurrent(root: HTMLElement): boolean {
  const previous = root.querySelector('table.ljc-est-table-previous');
  const current = root.querySelector('table.ljc-est-table-current');
  if (!previous || !current) return false;

  const originals = dataRows(previous).map((row) => ({ row, key: lineKey(row), used: false }));
  const nowRows = dataRows(current);

  // A line added after the first submission has nothing to strike out.
  const isNew = (row: HTMLTableRowElement): boolean =>
    row.classList.contains('ljc-est-row-new') || /(^|\s)ljc-est-row-new-/.test(row.className);
  const pairs = new Map<HTMLTableRowElement, HTMLTableRowElement>();

  // Same item and description (numbers blanked) first, in order.
  for (const row of nowRows) {
    if (isNew(row)) continue;
    const original = originals.find((o) => !o.used && o.key === lineKey(row));
    if (!original) continue;
    original.used = true;
    pairs.set(row, original.row);
  }
  // The item text itself was edited: only when exactly one line on each side is left over is it safe to call them the same line.
  const leftoverNow = nowRows.filter((row) => !isNew(row) && !pairs.has(row));
  const leftoverOriginal = originals.filter((o) => !o.used);
  if (leftoverNow.length === 1 && leftoverOriginal.length === 1) pairs.set(leftoverNow[0], leftoverOriginal[0].row);

  pairs.forEach((originalRow, row) => {
    for (const col of [RATE, QTY, TOTAL]) {
      const was = text(originalRow.cells[col]);
      const now = row.cells[col];
      if (!now) continue;
      const nowText = text(now);
      const same = num(was) !== null || num(nowText) !== null ? num(was) === num(nowText) : was === nowText;
      if (same) continue;
      const struck = row.ownerDocument.createElement('s');
      struck.className = 'ljc-est-orig';
      struck.textContent = was;
      now.classList.add('ljc-est-cell-changed');
      now.prepend(struck, row.ownerDocument.createTextNode(' '));
    }
  });
  if (pairs.size === 0) return false;

  // The original table moves into a closed section; its heading goes with it.
  const doc = previous.ownerDocument;
  const details = doc.createElement('details');
  details.className = 'ljc-est-original';
  const summary = doc.createElement('summary');
  summary.textContent = 'Original estimate as first sent';
  details.append(summary);
  const heading = previous.previousElementSibling;
  if (heading?.matches('h3.ljc-est-compare-heading')) heading.remove();
  previous.replaceWith(details);
  details.append(previous);

  const currentHeading = current.previousElementSibling;
  if (currentHeading?.matches('h3.ljc-est-compare-heading')) currentHeading.textContent = 'Estimate';
  // The page says what struck-out numbers are; the key to the row colours stays.
  root.querySelector('.ljc-est-legend-previous')?.remove();
  return true;
}
